/**
 * History Field Reset Registry
 * ----------------------------
 * Single source of truth for which form fields participate in `loadHistoryItem`.
 *
 * WHY THIS EXISTS
 * Feb 16, 2026 — we hit two consecutive field-bleed bugs:
 *   1. `deferredTopics` (Next Visit Topics) leaked from prior patient on history reload
 *   2. `peExceptions` (PE otherwise Unremarkable except) leaked the same way
 *
 * In both cases the bug was the same shape: `loadHistoryItem` did ad-hoc
 * per-field restoration and quietly forgot to wire up new fields. To eliminate
 * this entire class of bug, every form field that should participate in a
 * history reload now lives in this registry. Adding a new field requires
 * exactly ONE line here.
 *
 * GUARANTEE
 * Every field in this registry is ALWAYS either restored (from `item.<key>`)
 * or explicitly reset to its `default` on every history reload — no bleed.
 *
 * Each entry:
 *   - `key`:        the property on the backend history `item` (snake_case)
 *   - `setKey`:     the prop name of the React setter (e.g. `'setNotes'`)
 *   - `default`:    the empty/cleared value when `item[key]` is absent
 *   - `transform`:  optional `(item) => value` for derived restores
 *   - `description`: human-readable note for future devs
 */

export const HISTORY_FIELD_REGISTRY = [
  // ── Notes ──
  {
    key: 'original_notes',
    setKey: 'setNotes',
    default: '',
    description: 'Original raw dictation/typed notes',
  },

  // ── Patient demographics ──
  {
    key: 'patient_age',
    setKey: 'setPatientAge',
    default: '',
    transform: (item) => (item.patient_age != null ? String(item.patient_age) : ''),
    description: 'Patient age (always string in UI)',
  },
  {
    key: 'patient_gender',
    setKey: 'setPatientGender',
    default: '',
    description: 'Patient gender',
  },
  {
    key: 'patient_status',
    setKey: 'setPatientStatus',
    default: '',
    description: 'Active duty / veteran / dependent / civilian',
  },
  {
    key: 'patient_name',
    setKey: 'setPatientName',
    default: '',
    description: 'Patient display name',
  },
  {
    key: 'chief_complaint',
    setKey: 'setChiefComplaint',
    default: '',
    description: 'Chief complaint string',
  },

  // ── Clinical-note shape inputs (the box-bleed bug class) ──
  {
    key: 'deferred_topics',
    setKey: 'setDeferredTopics',
    default: '',
    description: 'Next Visit Topics box — Feb 16 bleed bug',
  },
  {
    key: 'pe_exceptions',
    setKey: 'setPeExceptions',
    default: '',
    description: 'PE otherwise Unremarkable except text — Feb 16 bleed bug',
  },

  // ── ICD-10 codes (derived from suggestions[]) ──
  {
    key: 'icd10_suggestions',
    setKey: 'setManualIcd10',
    default: '',
    transform: (item) => {
      const suggestions = item.icd10_suggestions;
      if (!suggestions || !suggestions.length) return '';
      return suggestions.map((c) => c.code || c).filter(Boolean).join(', ');
    },
    description: 'Manual ICD-10 input restored from generated suggestions array',
  },

  // ── Doc-type toggles (boolean based on presence of generated content) ──
  {
    key: 'clinical_note',
    setKey: 'setGenerateClinicalNote',
    default: false,
    transform: (item) => !!item.clinical_note,
    description: 'Re-enables clinical note toggle if a note was previously generated',
  },
  {
    key: 'admin_letter',
    setKey: 'setGenerateAdminLetter',
    default: false,
    transform: (item) => !!item.admin_letter,
    description: 'Re-enables admin letter toggle if a letter was previously generated',
  },
  {
    key: 'limitations',
    setKey: 'setGenerateLimitations',
    default: false,
    transform: (item) => !!item.limitations,
    description: 'Re-enables limitations toggle if limitations were previously generated',
  },

  // ── Imaging / referrals (arrays; restored if non-empty, else cleared to placeholder shape) ──
  {
    key: 'imaging_orders',
    setKey: 'setImagingOrders',
    default: [{ type: '', bodyPart: '' }],
    transform: (item) =>
      Array.isArray(item.imaging_orders) && item.imaging_orders.length > 0
        ? item.imaging_orders
        : [{ type: '', bodyPart: '' }],
    description: 'Imaging orders array; reset to single empty row when absent',
  },
  {
    key: 'specialties',
    setKey: 'setSpecialties',
    default: [''],
    transform: (item) =>
      Array.isArray(item.specialties) && item.specialties.length > 0
        ? item.specialties
        : [''],
    description: 'Referral specialties array; reset to single empty slot when absent',
  },
];

/**
 * Apply the registry to a loaded history item.
 *
 * @param {object} item    The full history doc from the backend
 * @param {object} setters Object mapping setter names → setState fns (e.g. { setNotes, setPatientAge, ... })
 * @returns {{ restored: string[], skipped: string[] }} Audit info — list of fields that fired vs. were no-ops (missing setter)
 */
export const applyHistoryRegistry = (item, setters) => {
  const restored = [];
  const skipped = [];
  for (const entry of HISTORY_FIELD_REGISTRY) {
    const setter = setters[entry.setKey];
    if (typeof setter !== 'function') {
      skipped.push(entry.setKey);
      continue;
    }
    const value = entry.transform
      ? entry.transform(item)
      : (item[entry.key] !== undefined && item[entry.key] !== null
          ? item[entry.key]
          : entry.default);
    setter(value);
    restored.push(entry.key);
  }
  return { restored, skipped };
};
